import { Autocomplete, TextField } from "@mui/material";
import { ReactNode } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { describedBy } from "./describedBy.ts";

export function AutocompleteFormField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TValue,
>({
  control,
  name,
  label,
  options,
  noOptionsText,
  getOptionKey,
  getOptionLabel,
  isOptionEqualToValue,
  disabled,
  ariaDescribedBy,
}: {
  control: Control<TFieldValues>;
  name: TName;
  label: ReactNode;
  options: Array<TValue>;
  noOptionsText?: ReactNode;
  getOptionKey?: (option: TValue) => string;
  getOptionLabel?: (option: TValue) => string;
  isOptionEqualToValue?: (option: TValue, value: TValue) => boolean;
  disabled?: boolean;
  ariaDescribedBy?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field, fieldState }) => {
        return (
          <Autocomplete
            options={options}
            noOptionsText={noOptionsText}
            getOptionKey={getOptionKey}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            fullWidth
            value={field.value}
            onChange={(_, value) => field.onChange(value)}
            disabled={field.disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  htmlInput: {
                    ...params.inputProps,
                    "aria-describedby": describedBy(
                      ariaDescribedBy,
                      !!fieldState.error?.message && `${params.id}-error`,
                    ),
                  },
                  formHelperText: { id: `${params.id}-error` },
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
